import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { SEO } from "../components/SEO";

// Types
interface FAQ {
  question: string;
  answer: string;
}

// Main FAQ Page Component
const FAQPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "study-abroad" | "migration" | "flight-booking"
  >("study-abroad");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const studyAbroadFAQs: FAQ[] = [
    {
      question: "How much does it cost to study abroad?",
      answer:
        "Costs vary by country, university, and course. Tuition ranges from $5,000 to $35,000 per year, with living expenses depending on location. We help you budget realistically and explore scholarships or affordable options.",
    },
    {
      question: "Which universities have affordable tuition?",
      answer:
        "We recommend reputable universities in Canada, the UK, Australia, and Europe that offer quality education within your budget.",
    },
    {
      question: "Can I work while studying?",
      answer:
        "Yes. Many countries allow part-time work for students. We guide you on legal requirements and opportunities.",
    },
    {
      question: "Can I stay in a country after studying?",
      answer:
        "Some destinations offer post-study work visas or pathways to permanent residency. We provide guidance on your options.",
    },
    {
      question: "Can I bring my dependents along?",
      answer:
        "In certain cases, students can bring spouses or children. We assist with dependent visa applications.",
    },
    {
      question: "Can I get a visa if I've had a previous refusal?",
      answer:
        "Yes. We analyze your prior application, identify reasons for rejection, and help strengthen your new submission.",
    },
    {
      question: "What if I've been banned before   can I still get a visa?",
      answer:
        "Visa bans can affect eligibility, but we assess your situation and guide you on the best approach to reapply successfully.",
    },
    {
      question: "Do I need an English test to study abroad?",
      answer:
        "Most English-speaking universities require exams like IELTS, TOEFL, or PTE. We advise and help you prepare to meet score requirements.",
    },
    {
      question: "How long does the study abroad process take?",
      answer:
        "The process can take several months, depending on deadlines, documents, visas, and scholarships. We provide a clear timeline for your application.",
    },
    {
      question: "What countries can I apply to through BeginPack?",
      answer:
        "We assist students applying to Australia, New Zealand, Canada, the UK, and Europe.",
    },
    {
      question: "Can you help with scholarships?",
      answer:
        "Yes. We guide you to identify suitable scholarships, prepare applications, and increase your chances of financial support.",
    },
    {
      question: "Do you help with accommodation or travel?",
      answer:
        "Absolutely. We help you find safe housing and assist with flight bookings and travel planning.",
    },
    {
      question: "What are my chances of getting a visa?",
      answer:
        "Visa approval is at the embassy's discretion, but with our guidance, we have a 98% success rate, ensuring your application is complete, accurate, and well-presented.",
    },
  ];

  const migrationFAQs: FAQ[] = [
    {
      question: "What migration services does BeginPack offer?",
      answer:
        "We provide comprehensive migration support for individuals, skilled workers, and families, including visa applications, work permits, skilled worker programs, and permanent residency guidance.",
    },
    {
      question:
        "How do I know if I'm eligible for a visa or migration program?",
      answer:
        "We conduct a detailed visa eligibility assessment, reviewing your profile and goals to recommend the most suitable pathways.",
    },
    {
      question: "Can BeginPack help if I've had a visa refusal before?",
      answer:
        "Yes. We review your previous application, identify issues, and help strengthen your new application to improve your chances of approval.",
    },
    {
      question: "Can I bring my family with me when migrating?",
      answer:
        "In many cases, spouses and children can be included. We guide you through dependent visa applications and eligibility requirements.",
    },
    {
      question: "How long does the migration process take?",
      answer:
        "Processing times vary depending on the country and visa type. We provide a clear timeline and assist in preparing all required documents to avoid delays.",
    },
    {
      question:
        "Can you help with skilled worker or permanent residency programs?",
      answer:
        "Yes. We guide professionals through skilled worker programs and permanent residency applications, helping you meet eligibility requirements and submit accurate applications.",
    },
    {
      question: "Do you assist with documentation and compliance?",
      answer:
        "Absolutely. We ensure all your migration paperwork is complete, accurate, and compliant with government regulations, reducing the risk of delays or rejections.",
    },
    {
      question: "How successful are BeginPack's migration applications?",
      answer:
        "While final approval rests with immigration authorities, our experience and thorough process have earned us a high success rate, ensuring your application is well-prepared.",
    },
  ];

  const flightBookingFAQs: FAQ[] = [
    {
      question: "Can BeginPack help me book flights?",
      answer:
        "Yes! We provide fast, reliable, and affordable flight booking services for students, travelers, and families, helping you find the best fares, routes, and airlines.",
    },
    {
      question: "Do you handle round-trip and multi-city bookings?",
      answer:
        "Absolutely. We assist with one-way, round-trip, and multi-city travel arrangements tailored to your schedule and budget.",
    },
    {
      question: "Can I change or cancel my flight bookings through BeginPack?",
      answer:
        "Yes. We guide you through airline policies for cancellations, rescheduling, and refunds to make the process smooth.",
    },
    {
      question: "Do you offer travel insurance?",
      answer:
        "Yes. We provide guidance on travel insurance to ensure you're covered for health, luggage, and emergencies during your journey.",
    },
    {
      question: "Can BeginPack help with airport transfers and local travel?",
      answer:
        "Yes. We provide advice and assistance with airport pickups, transportation options, and travel planning in your destination country.",
    },
    {
      question:
        "Can BeginPack assist with both migration and flight arrangements together?",
      answer:
        "Yes. We offer end-to-end support, from securing your visa and migration documents to booking flights and planning travel logistics.",
    },
  ];

  const handleTabChange = (
    tab: "study-abroad" | "migration" | "flight-booking",
  ) => {
    setActiveTab(tab);
    setOpenIndex(null); // Reset open FAQ when switching tabs
  };

  const getActiveFAQs = () => {
    switch (activeTab) {
      case "study-abroad":
        return studyAbroadFAQs;
      case "migration":
        return migrationFAQs;
      case "flight-booking":
        return flightBookingFAQs;
      default:
        return studyAbroadFAQs;
    }
  };

  return (
    <>
      <SEO
        title="FAQ"
        description="Find answers to frequently asked questions about study abroad, migration services, and flight booking. Learn about visa requirements, costs, scholarships, and more."
        ogTitle="Frequently Asked Questions - BeginPack Ltd"
        ogDescription="Get answers to common questions about studying abroad, visa applications, migration services, and flight booking. Expert guidance for your international journey."
      />
      {/* Hero Section */}
      <div className="relative bg-cover bg-center bg-no-repeat py-20 bg-gradient-to-br from-[#004E89] to-[#800020] md:py-28 lg:py-32">
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-['Plus_Jakarta_Sans'] text-xs md:text-sm tracking-widest text-white mb-6 uppercase antialiased mt-2 ">
            GET ANSWERS
          </p>
          <h1 className="font-['Poppins'] text-2xl sm:text-2xl md:text-3xl lg:text-3xl font-bold text-white mx-auto">
            Frequently Asked Questions
          </h1>
        </div>
      </div>

      <section className="min-h-screen bg-gradient-to-b from-gray-50 to-white px-4 pt-0 pb-6 md:pb-12 lg:pb-16">
        <div className="max-w-3xl mx-auto">
          {/* Tabs */}
          <div className="flex flex-nowrap justify-center mb-6 md:mb-10 mt-4 overflow-x-auto">
            <button
              onClick={() => handleTabChange("study-abroad")}
              className={`px-2 py-2 mx-2 mb-2 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${
                activeTab === "study-abroad"
                  ? "border-[#800020] text-[#800020]"
                  : "border-transparent text-gray-700 hover:text-[#800020]"
              }`}
            >
              Study Abroad
            </button>
            <button
              onClick={() => handleTabChange("migration")}
              className={`px-2 py-2 mx-2 mb-2 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${
                activeTab === "migration"
                  ? "border-[#800020] text-[#800020]"
                  : "border-transparent text-gray-700 hover:text-[#800020]"
              }`}
            >
              Migration
            </button>
            <button
              onClick={() => handleTabChange("flight-booking")}
              className={`px-2 py-2 mx-2 mb-2 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${
                activeTab === "flight-booking"
                  ? "border-[#800020] text-[#800020]"
                  : "border-transparent text-gray-700 hover:text-[#800020]"
              }`}
            >
              Flight Booking
            </button>
          </div>

          {/* FAQ Items */}
          <div className="space-y-2 mb-6">
            {getActiveFAQs().map((faq, index) => {
              const isOpen = openIndex === index;

              return (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-start sm:items-center justify-between gap-4 p-4 sm:p-5 lg:p-6 text-left hover:bg-gray-50 transition-colors"
                    aria-expanded={isOpen}
                  >
                    <span className="font-['Plus_Jakarta_Sans'] font-semibold text-gray-900 text-sm sm:text-base tracking-tight antialiased flex-1">
                      <span className="text-gray-400 mr-2">{index + 1}.</span>
                      {faq.question}
                    </span>

                    <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#800020] flex items-center justify-center transition-transform duration-200 hover:scale-110">
                      {isOpen ? (
                        <Minus
                          className="w-4 h-4 text-white"
                          strokeWidth={2.5}
                        />
                      ) : (
                        <Plus
                          className="w-4 h-4 text-white"
                          strokeWidth={2.5}
                        />
                      )}
                    </div>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="px-4 sm:px-5 lg:px-6 pb-5 lg:pb-6">
                      <p className="font-['Plus_Jakarta_Sans'] text-gray-600 text-sm sm:text-base tracking-tight antialiased leading-relaxed pl-6">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQPage;
